import type { FilterFn, Row, SortingFn, SortingState } from '@tanstack/react-table'

import type { MatterArtifactDraftListRow } from '@/drafting/hooks/useMatterArtifactDrafts'

/** Column ids of the Drafts table. */
export const kDraftsTableColumn = {
  NAME: 'name',
  UPDATED_AT: 'updatedAt',
  CREATOR: 'creatorLabel',
  ACTIONS: 'actions',
} as const

/** Newest first. */
export const kDefaultDraftsSorting: SortingState = [{ id: kDraftsTableColumn.UPDATED_AT, desc: true }]

/**
 * Whether a draft matches a search query: case-insensitive, whitespace-split, every term appearing
 * in the name or the creator.
 *
 * The creator is matched on the label the row shows, so a user's own drafts match `me` rather than
 * their name or email.
 */
export function matchesDraftSearch(row: MatterArtifactDraftListRow, query: string): boolean {
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((term) => term.length > 0)
  if (terms.length === 0) return true

  const searchableText = `${row.name} ${row.creatorLabel ?? ''}`.toLowerCase()
  return terms.every((term) => searchableText.includes(term))
}

/**
 * Keeps drafts sharing a timestamp or a name from swapping places between renders. TanStack negates
 * a comparator for a descending sort, so this flips with the direction — still deterministic, which
 * is all the order needs to be.
 */
function tieBreak(a: MatterArtifactDraftListRow, b: MatterArtifactDraftListRow): number {
  return a.artifactVersionId.localeCompare(b.artifactVersionId)
}

export function compareDraftsByName(a: MatterArtifactDraftListRow, b: MatterArtifactDraftListRow): number {
  return a.name.localeCompare(b.name) || tieBreak(a, b)
}

export function compareDraftsByUpdatedAt(a: MatterArtifactDraftListRow, b: MatterArtifactDraftListRow): number {
  return a.updatedAt - b.updatedAt || tieBreak(a, b)
}

/**
 * Creators A-Z. An unresolved creator never reaches here: its accessor yields `undefined`, which the
 * column's `sortUndefined` handles.
 */
export function compareDraftsByCreator(a: MatterArtifactDraftListRow, b: MatterArtifactDraftListRow): number {
  return (a.creatorLabel ?? '').localeCompare(b.creatorLabel ?? '') || tieBreak(a, b)
}

/** Multi-term AND filter across the two searchable columns, in TanStack's global-filter shape. */
export const draftsGlobalFilter: FilterFn<MatterArtifactDraftListRow> = (row, _columnId, filterValue: string) => {
  return matchesDraftSearch(row.original, filterValue)
}

/** Wraps a row comparator so TanStack can own the direction and the `undefined` handling. */
function toSortingFn(compare: (a: MatterArtifactDraftListRow, b: MatterArtifactDraftListRow) => number): SortingFn<MatterArtifactDraftListRow> {
  return (a: Row<MatterArtifactDraftListRow>, b: Row<MatterArtifactDraftListRow>) => compare(a.original, b.original)
}

export const draftsNameSortingFn = toSortingFn(compareDraftsByName)
export const draftsUpdatedAtSortingFn = toSortingFn(compareDraftsByUpdatedAt)
export const draftsCreatorSortingFn = toSortingFn(compareDraftsByCreator)

/** Reads as the order the list is in, unlike the header buttons, whose labels name the next click. */
const kDraftsSortDescriptions: Record<string, { column: string; ascending: string; descending: string }> = {
  [kDraftsTableColumn.NAME]: { column: 'Draft Name', ascending: 'A to Z', descending: 'Z to A' },
  [kDraftsTableColumn.UPDATED_AT]: { column: 'Last Modified', ascending: 'oldest first', descending: 'newest first' },
  [kDraftsTableColumn.CREATOR]: { column: 'Created By', ascending: 'A to Z', descending: 'Z to A' },
}

/** The order the list is in, for the live region. Empty when nothing is sorted. */
export function describeDraftsSorting(sorting: SortingState): string {
  const [active] = sorting
  const description = active ? kDraftsSortDescriptions[active.id] : undefined
  if (!active || !description) return ''

  return `Sorted by ${description.column}, ${active.desc ? description.descending : description.ascending}`
}
